import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';
import { Loader, Center } from '@/components/ui';
import { BACKEND_ROLES } from '@/config/permissions';
import { LeaveApproverGuard } from './components/leave-approvals/LeaveApproverGuard';

// ===== Eager: My HR landing pages (every authenticated user reaches these) =====
import EmployeeDashboardPage from './dashboard';
import EmployeeCalendarPage from './calendar';
import MyAnnouncementsPage from './announcements';
import MyProfilePage from './my-profile';
import MyLeavesPage from './my-leaves';
import MyFilesPage from './my-files';
import MyDutiesPage from './my-duties';
import MyPoliciesPage from './my-policies';

// ===== Lazy: detail/wizard pages and admin module =====
const MyLeaveDetailPage = lazy(() => import('./my-leaves/[id]'));
const EditLeavePage = lazy(() => import('./my-leaves/[id]/edit'));
const NewLeavePage = lazy(() => import('./my-leaves/new'));
const MyPolicyDetailPage = lazy(() => import('./my-policies/[id]'));
const MySignatureHistoryPage = lazy(() => import('./my-policies/history'));

// Leave approvals (line approvers: data-gated by LeaveApproverGuard, no role)
const LeaveApprovalsPage = lazy(() => import('./approvals'));
const LeaveApprovalDeepLinkPage = lazy(() => import('./approvals/[id]'));

// Admin pages (HRAdmin role required)
const AdminDashboardPage = lazy(() => import('./admin/dashboard'));
const AdminEmployeesPage = lazy(() => import('./admin/employees'));
const AdminEmployeeDetailPage = lazy(() => import('./admin/employees/[id]'));
const AdminCreateEmployeePage = lazy(() => import('./admin/employees/create'));
const AdminLeavesPage = lazy(() => import('./admin/leaves'));
const AdminLeaveDetailPage = lazy(() => import('./admin/leaves/[id]'));
const AdminPoliciesPage = lazy(() => import('./admin/policies'));
const AdminPolicyDetailPage = lazy(() => import('./admin/policies/[id]'));
const AdminCreatePolicyPage = lazy(() => import('./admin/policies/create'));
const AdminPolicyAssignmentsPage = lazy(() => import('./admin/policy-assignments'));
const AdminAnnouncementsPage = lazy(() => import('./admin/announcements'));
const AdminEmployeeFilesPage = lazy(() => import('./admin/employee-files'));
const AdminPaySpacePage = lazy(() => import('./admin/payspace'));
const AdminSettingsPage = lazy(() => import('./admin/settings'));
const AdminLookupsLandingPage = lazy(() => import('./admin/lookups'));
const AdminLookupCategoryPage = lazy(() => import('./admin/lookups/[category]'));
const AdminLookupManagementPage = lazy(() => import('./admin/lookups/[category]/[lookup]'));
const AdminFinalApprovalsPage = lazy(() => import('./admin/final-approvals'));
const AdminLeaveApprovalRulesPage = lazy(() => import('./admin/leave-approval-rules'));

const LoadingFallback = () => (
  <Center h={400}><Loader size="lg" /></Center>
);

const HR_ADMIN_ROLES = [BACKEND_ROLES.HR_ADMIN];

/**
 * HRManagement Routes
 *
 * Role separation:
 *  - Admin pages under /admin/* require the HRAdmin role (general Admin is locked out
 *    in line with the "no HR data leakage to non-HR admins" requirement).
 *  - The "My HR" landing pages reach the staff portal endpoints which already enforce
 *    Staff-role + ownership server-side, so any authenticated user can access them
 *    (HR Admins also have a personal HR experience).
 */
export default function HRManagementRoutes() {
  return (
    <MainLayout domainKey="members">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Default landing — every authenticated user lands on the personal dashboard */}
          <Route index element={<Navigate to="dashboard" replace />} />

          {/* My HR — staff portal pages */}
          <Route path="dashboard" element={<EmployeeDashboardPage />} />
          <Route path="calendar" element={<EmployeeCalendarPage />} />
          <Route path="announcements" element={<MyAnnouncementsPage />} />
          <Route path="my-profile" element={<MyProfilePage />} />
          <Route path="my-leaves" element={<MyLeavesPage />} />
          <Route path="my-leaves/new" element={<NewLeavePage />} />
          <Route path="my-leaves/:id" element={<MyLeaveDetailPage />} />
          <Route path="my-leaves/:id/edit" element={<EditLeavePage />} />
          <Route path="my-files" element={<MyFilesPage />} />
          <Route path="my-duties" element={<MyDutiesPage />} />
          <Route path="my-policies" element={<MyPoliciesPage />} />
          <Route path="my-policies/history" element={<MySignatureHistoryPage />} />
          <Route path="my-policies/:id" element={<MyPolicyDetailPage />} />

          {/* Leave approvals: any signed-in user the data links to (line approvers, final approvers) */}
          <Route path="approvals" element={<LeaveApproverGuard><LeaveApprovalsPage /></LeaveApproverGuard>} />
          <Route path="approvals/:id" element={<LeaveApproverGuard><LeaveApprovalDeepLinkPage /></LeaveApproverGuard>} />

          {/* HR Admin — HRAdmin role required */}
          <Route path="admin" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><Navigate to="dashboard" replace /></ProtectedRoute>} />
          <Route path="admin/dashboard" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminDashboardPage /></ProtectedRoute>} />
          <Route path="admin/employees" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminEmployeesPage /></ProtectedRoute>} />
          <Route path="admin/employees/create" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminCreateEmployeePage /></ProtectedRoute>} />
          <Route path="admin/employees/:id" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminEmployeeDetailPage /></ProtectedRoute>} />
          <Route path="admin/employee-files" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminEmployeeFilesPage /></ProtectedRoute>} />
          <Route path="admin/leaves" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminLeavesPage /></ProtectedRoute>} />
          <Route path="admin/leaves/:id" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminLeaveDetailPage /></ProtectedRoute>} />
          <Route path="admin/final-approvals" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminFinalApprovalsPage /></ProtectedRoute>} />
          <Route path="admin/leave-approval-rules" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminLeaveApprovalRulesPage /></ProtectedRoute>} />
          <Route path="admin/policies" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminPoliciesPage /></ProtectedRoute>} />
          <Route path="admin/policies/create" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminCreatePolicyPage /></ProtectedRoute>} />
          <Route path="admin/policies/:id" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminPolicyDetailPage /></ProtectedRoute>} />
          <Route path="admin/policy-assignments" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminPolicyAssignmentsPage /></ProtectedRoute>} />
          <Route path="admin/announcements" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminAnnouncementsPage /></ProtectedRoute>} />
          <Route path="admin/payspace" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminPaySpacePage /></ProtectedRoute>} />
          <Route path="admin/settings" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminSettingsPage /></ProtectedRoute>} />
          <Route path="admin/lookups" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminLookupsLandingPage /></ProtectedRoute>} />
          <Route path="admin/lookups/:category" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminLookupCategoryPage /></ProtectedRoute>} />
          <Route path="admin/lookups/:category/:lookup" element={<ProtectedRoute requiredRoles={HR_ADMIN_ROLES}><AdminLookupManagementPage /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
